import React, { useState, useEffect } from "react";
import { Box, CircularProgress, Divider } from "@mui/material";
import VcDatePicker from "Components/UI/DatePicker/DatePicker";
import { useTranslation } from "react-i18next";
import moment from "moment";
import images from "../../../../Assets/images";
import { useApiCall } from "Hooks/useApiCall";
import { getLocalStorage } from "Network/ApiService";
import { ls_vars } from "Library/Constants";
import "./style.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
} from "recharts";
import styled from "styled-components";
import { getEnvironmentReports } from "Network/Core/EquipmentMaster/equipment";
import { convertDistance, convertWeight, extractNumber, formatDate, toSnakeCase } from "Library/Utils";
import { useTranslationCheck } from "Hooks/useTranslationCheck";
const countryUom = getLocalStorage(ls_vars.COUNTRY_UOM);

// First, update your ChartData interface to match the new structure
interface TimeSeriesData {
  distance: {
    total: Array<{ month: number; value: number }>;
    average: Array<{ month: number; value: number }>;
  };
  co2: {
    total: Array<{ month: number; value: number }>;
    average: Array<{ month: number; value: number }>;
  };
}

interface ChartData {
  distanceByVehicle: Array<{
    vehicleType: string;
    fuelType: string;
    distance: number;
  }>;
  co2_byVehicle: Array<{
    vehicleType: string;
    fuelType: string;
    co2: number;
  }>;
  timeSeries: TimeSeriesData;
  units: {
    distance: string;
    co2: string;
  };
}

interface SummaryMetrics {
  totalDistance: { value: number; unit: string };
  averageDistance: { value: number; unit: string };
  totalCo2: { value: number; unit: string };
  averageCo2: { value: number; unit: string };
  totalTrips: number;
}

interface PerformanceData {
  creatorOrganizationName: string;
  numDeliveries: number;
  totalDistance: number;
  avgDistance: number;
  totalCo2: number;
  avgCo2: number;
  efficiencyScore: number;
  rank: number;
}

interface EnvironmentReportData {
  summaryMetrics: SummaryMetrics;
  charts: ChartData;
  performanceByRecipient: PerformanceData[];
  roundTripEnabled: Boolean;
  units: {
    distance: string;
    co2: string;
  };
}

const metricConfig = {
  totalDistance: {
    backgroundColor: "#EAF9DE",
    textColor: "#085E50",
    label: "Total Distance",
  },
  averageDistance: {
    backgroundColor: "#E3F1FF",
    textColor: "#004996",
    label: "Average Distance",
  },
  totalCo2: {
    backgroundColor: "#EFF3F6",
    textColor: "#3F4B57",
    label: "Total CO2",
  },
  averageCo2: {
    backgroundColor: "#F8F0FF",
    textColor: "#6B2D9C",
    label: "Average CO2",
  },
  totalTrips: {
    backgroundColor: "#FFF4E5",
    textColor: "#B54708",
    label: "Total Trips",
  },
};

const ChartCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  height: 400px;
  display: flex;
  flex-direction: column;

  h4 {
    font-family: "Inter";
    font-style: normal;
    margin: 0 0 20px 0;
    color: #576473;
    font-size: 16px;
    font-weight: 600;
  }

  .chart-container {
    flex: 1;
    min-height: 300px;
    font-family: "Inter";
    font-style: normal;
  }

  // Add custom tooltip styles
  .recharts-tooltip-wrapper {
    font-family: "Inter";
    font-style: normal;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    padding: 8px;
  }
`;

const VehicleDistanceChart: React.FC<{ data: ChartData['distanceByVehicle']; unit: string; t: (key: string) => string }> = ({ data, unit, t }) => (
  <ChartCard>
    <h4>{t("Translation.distance_by_vehicle_type")}</h4>
    <div className="chart-container">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="horizontal"
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="category"
            dataKey="vehicleType"
            height={50}
          />
          <YAxis
            type="number"
            label={{
              value: `${t("Translation.distance")} (${unit})`,
              angle: -90,
              position: 'insideLeft'
            }}
          />
          <Tooltip />
          <Legend />
          <Bar
            dataKey="distance"
            fill="#8884d8"
            name={t("Translation.distance")}
            barSize={30}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </ChartCard>
);

const VehicleCO2Chart: React.FC<{ data: ChartData['co2_byVehicle']; unit: string; t: (key: string) => string }> = ({ data, unit, t }) => (
  <ChartCard>
    <h4>{t("Translation.co2_emission_by_vehicle_type")}</h4>
    <div className="chart-container">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="horizontal"
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="category"
            dataKey="vehicleType"
            height={50}
          />
          <YAxis
            type="number"
            label={{
              value: `${t("Translation.co2")} (${unit})`,
              angle: -90,
              position: 'insideLeft'
            }}
          />
          <Tooltip />
          <Legend />
          <Bar
            dataKey="co2"
            fill="#82ca9d"
            name={t("Translation.co2_emissions")}
            barSize={30}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </ChartCard>
);

const TimeSeriesDistanceChart: React.FC<{
  data: TimeSeriesData["distance"];
  unit: string;
  t: (key: string) => string;
}> = ({ data, unit, t }) => {
  // Combine total and average data for the chart
  const combinedData = data.total.map((totalItem) => {
    const avgItem = data.average.find((avg) => avg.month === totalItem.month);
    return {
      month: moment()
        .month(totalItem.month - 1)
        .format("MMM"),
      total: totalItem.value,
      average: avgItem?.value || 0,
    };
  });

  return (
    <div className="chart-card">
      <h4>{t("Translation.distance_trendes")}</h4>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={combinedData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis
            label={{
              value: `${t("Translation.distance")} (${unit})`,
              angle: -90,
              position: "insideLeft",
            }}
          />
          <Tooltip />
          <Legend />
          <Line
            type="monotone"
            dataKey="total"
            stroke="#8884d8"
            name={t("Translation.total_distance")}
            strokeWidth={2}
          />
          <Line
            type="monotone"
            dataKey="average"
            stroke="#82ca9d"
            name={t("Translation.average_distance")}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const TimeSeriesCO2Chart: React.FC<{ data: TimeSeriesData["co2"]; unit: string; t: (key: string) => string }> = ({
  data,
  unit,
  t,
}) => {
  const combinedData = data.total.map((totalItem) => {
    const avgItem = data.average.find((avg) => avg.month === totalItem.month);
    return {
      month: moment()
        .month(totalItem.month - 1)
        .format("MMM"),
      total: totalItem.value,
      average: avgItem?.value || 0,
    };
  });

  return (
    <div className="chart-card">
      <h4>{t("Translation.co2_emission_trendes")}</h4>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={combinedData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis
            label={{ value: `${t("Translation.co2")} (${unit})`, angle: -90, position: "insideLeft" }}
          />
          <Tooltip />
          <Legend />
          <Line
            type="monotone"
            dataKey="total"
            stroke="#8884d8"
            name={t("Translation.total_co2")}
            strokeWidth={2}
          />
          <Line
            type="monotone"
            dataKey="average"
            stroke="#82ca9d"
            name={t("Translation.average_co2")}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Custom tooltip component (optional)
const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '8px',
        border: '1px solid #ccc',
        borderRadius: '4px'
      }}>
        <p>{`Vehicle Type: ${label}`}</p>
        <p>{`${payload[0].name}: ${payload[0].value}`}</p>
      </div>
    );
  }
  return null;
};

const EnvironmentReport: React.FC = () => {
  const { t, tCheck } = useTranslationCheck();
  const { call } = useApiCall();
  const project = getLocalStorage(ls_vars.PROJECT_DATA);
  const region = getLocalStorage(ls_vars.REGION_ID);
  const user = getLocalStorage(ls_vars.USER_DATA);
  const [startDate, setStartDate] = useState<any>("");
  const [endDate, setEndDate] = useState<any>("");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<EnvironmentReportData | null>(
    null
  );

  const handleDateChange = (date: Date | null, type: "start" | "end") => {
    if (type === "start") {
      setStartDate(date);
    } else {
      setEndDate(date);
    }
  };



  const renderMetricBox = (key: string, data: any) => {
    const config = metricConfig[key as keyof typeof metricConfig];
    const value = typeof data === "object" ? data.value : data;
    const unit = typeof data === "object" ? data.unit : "";

    return (
      <div
        className="metric-box"
        key={key}
        style={{
          backgroundColor: config.backgroundColor,
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: config.textColor,
            fontSize: "24px",
            margin: "0 0 8px 0",
          }}
        >
          {
            typeof value === "number"
              ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
              : value
          }
          {unit && (
            <>
            <span style={{ fontSize: "16px", marginLeft: "4px" }}>{unit}</span>
            </>
          )}
        </h2>
        <h6
          style={{
            textAlign: "center",
            color: config.textColor,
            margin: 0,
            fontSize: "14px",
            fontWeight: "normal",
          }}
        >
          {tCheck(toSnakeCase(config.label))}
        </h6>
      </div>
    );
  };


  useEffect(() => {
    const fetchEnvironmentReport = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const formattedStartDate = startDate
          ? moment(startDate).format("YYYY-MM-DD")
          : "";
        const formattedEndDate = endDate
          ? moment(endDate).format("YYYY-MM-DD")
          : "";

        call(
          () =>
            getEnvironmentReports(
              {
                startDate: formattedStartDate,
                endDate: formattedEndDate,

              },
              region,
              project.ref
            ),
          (response: any) => {
            setReportData(response);
          }
        );
      } catch (err) {
        setError("Failed to fetch equipment usage report");
        console.error("Error fetching equipment usage report:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (startDate && endDate && region) {
      fetchEnvironmentReport();
    }
  }, [startDate, endDate, region]);

  useEffect(() => {
    let today = new Date();
    let endDate = new Date();
    // Get one month after date
    endDate.setMonth(today.getMonth() + 1);
    endDate.setHours(0, 0, 0, 0);

    setStartDate(formatDate(today));
    setEndDate(formatDate(endDate));
  }, []);

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <h3 className="page-title">{t("Translation.environment_reports")}</h3>
      </div>

      <div className="page-content custom-list-div ">
        {/* Fixed Sections (Project Info, Date Range, Search) */}
        <div style={styles.fixedSection}>
          {/* Date Range and Search */}
          <div className="booking-list-form">
            <Box
              sx={{
                padding: "0 24px 22px 24px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              {/* Single row with date filters and search */}
              <Box
                sx={{
                  display: "flex",
                  gap: "16px",
                  flexWrap: "wrap",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {/* Date picker group */}
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "nowrap",
                }}>
                  <Box className="date-box-frontend">
                    <VcDatePicker
                      date={startDate}
                      onDateChange={(val: Date | null) =>
                        handleDateChange(val, "start")
                      }
                    />
                  </Box>
                  <Box className="to" sx={{ marginBottom: 0 }}>
                    {t("Translation.to")}
                  </Box>
                  <Box className="date-box-frontend">
                    <VcDatePicker
                      date={endDate}
                      onDateChange={(val) => handleDateChange(val, "end")}
                      minDate={new Date(startDate || new Date())}
                    />
                  </Box>
                </Box>


              </Box>
            </Box>
          </div>
          <Divider />
        </div>

        {/* Scrollable Content */}
        <div style={styles.scrollableContent}>
          {/* Metrics Display */}
          {isLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <CircularProgress />
            </div>
          ) : (
            reportData && (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "20px",
                    marginBottom: "30px",
                    marginTop: "20px",
                  }}
                >
                  {Object.entries(reportData?.summaryMetrics)?.map(
                    ([key, value]) => {
                      return renderMetricBox(key, value)
                    }
                  )}
                </div>

                {/* Charts Section */}

                <div style={chartStyles.chartsContainer}>
                  <VehicleDistanceChart 
                    data={reportData.charts.distanceByVehicle} 
                    unit={reportData.charts.units?.distance || reportData.units?.distance || 'km'} 
                    t={t}
                  />
                  <VehicleCO2Chart 
                    data={reportData.charts.co2_byVehicle} 
                    unit={reportData.charts.units?.co2 || reportData.units?.co2 || 'kg'} 
                    t={t}
                  />
                  <TimeSeriesDistanceChart 
                    data={reportData.charts.timeSeries.distance} 
                    unit={reportData.charts.units?.distance || reportData.units?.distance || 'km'} 
                    t={t}
                  />
                  <TimeSeriesCO2Chart 
                    data={reportData.charts.timeSeries.co2} 
                    unit={reportData.charts.units?.co2 || reportData.units?.co2 || 'kg'} 
                    t={t}
                  />
                </div>
                {/* Performance Table */}

                <div className="table-container" style={{ marginBottom: "30px" }}>
                  <h4 style={{ marginBottom: "15px" }}>
                    {t("Translation.performance_by_recipient")}
                  </h4>
                  <div style={{ overflowX: "auto" }} className="table-responsive"> 
                    {" "}
                    {/* Makes table horizontally scrollable if needed */}
                    <table
                      className="display nowrap dataTable w-100"
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: "800px",
                      }}
                    >
                      <thead>
                        <tr>
                          <th>{t("Translation.organization")}</th>
                          <th>{t("Translation.deliveries")}</th>
                          <th>
                            {t("Translation.total_distance")} ({reportData.units?.distance || 'km'})
                          </th>
                          <th>{t("Translation.avg_distance")} ({reportData.units?.distance || 'km'})</th>
                          <th>{t("Translation.total_co2")} ({reportData.units?.co2 || 'kg'})</th>
                          <th>{t("Translation.efficiency_score")}</th>
                          <th>{t("Translation.rank")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.performanceByRecipient.map(
                          (item, index) => (
                            <tr key={index}>
                              <td>
                                {item.creatorOrganizationName}
                              </td>
                              <td>
                                {item.numDeliveries}
                              </td>
                              <td>
                                {item.totalDistance || "-"}
                              </td>
                              <td>
                                {item.avgDistance || "-"}
                              </td>
                              <td>
                                {item.totalCo2 || "-"}
                              </td>
                              <td>
                                {item.efficiencyScore}
                              </td>
                              <td>{item.rank}</td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  dateRangeContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "14px",
  },
  datePickerWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  dateBoxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
  },
  searchImage: {
    position: "relative" as "relative",
    height: "20px",
    width: "20px",
    left: "30px",
  },
  tableHeader: {
    backgroundColor: "#EFF3F6",
    color: "#576473",
    padding: "12px",
    textAlign: "left" as "left",
  },
  tableCell: {
    padding: "12px",
    borderBottom: "1px solid #e0e0e0",
  },
  pageWrapper: {
    height: "100vh",
    display: "flex",
    flexDirection: "column" as "column",
  },
  pageHeader: {
    padding: "20px 24px",
    backgroundColor: "#fff",
    borderBottom: "1px solid #e0e0e0",
    flexShrink: 0, // Prevents header from shrinking
  },
  pageContent: {
    flex: 1,
    overflow: "hidden", // Prevents double scrollbars
    display: "flex",
    flexDirection: "column" as "column",
  },
  scrollableContent: {
    flex: 1,
    overflow: "auto", // Makes the content scrollable
    padding: "0 24px 24px 24px",
  },
  fixedSection: {
    backgroundColor: "#fff",
    zIndex: 1,
    flexShrink: 0, // Prevents section from shrinking
  }
};

const chartStyles = {
  chartsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px",
    padding: "20px",
  },
  chartCard: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
};
export default EnvironmentReport;
